import type { LogoOption } from '@monthio/design-system'
import type { BankDto } from '#modules/Bank/Bank.types'

import {
	FlowPage,
	H2,
	LogoSearchBar,
	PrimaryButton,
	SecondaryButton,
	SPACING,
} from '@monthio/design-system'
import { LoadingScreen, searchArray } from '@monthio/react-components'
import { BankServiceProviders, routeCodes } from '#modules/Bank/Bank.types'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { styled } from 'styled-components'

import type { BankSelectorPageLayoutProps } from './BankSelectorPage'

import { BankSelect } from './BankSelect'
import { ButtonContainer } from './bankSelectorStyles'
import { MonthioConsent } from './MonthioConsent'

const handleSearch = (
	banks: BankDto[],
	isProduction: boolean,
	searchTerm?: string,
): Promise<LogoOption[]> => {
	const rawResult = searchArray(
		banks,
		searchTerm,
		{ key: 'displayName' },
		{ maxResults: 20, allowErrors: false },
	) as BankDto[]

	let result: BankDto[]
	switch (true) {
		case searchTerm === 'monthiotest!':
			result = banks.filter((bank) =>
				bank.displayName.toLowerCase().includes('test'),
			)
			break
		case isProduction:
			result = rawResult.filter(
				(bank) => !bank.displayName.toLowerCase().includes('test'),
			)
			break
		default:
			result = rawResult
	}

	const options = result.map((bank) => ({
		id: bank.id.toString(),
		logoSrc: bank.iconUrl,
		name: bank.displayName,
	}))
	return Promise.resolve(options)
}

const Section = styled.div<{ $gap?: keyof typeof SPACING }>`
	display: flex;
	flex-direction: column;
	gap: ${(p) => SPACING[p.$gap ?? 'l']};
`

export const BankSelectorPageUpdatedLayout = ({
	bankSessions,
	history,
	demoProfile,
	banks,
	recentlySearchedInitialCooldownComplete,
	maxGridSize,
	onSelectBank,
	consentGiven,
	translatedConsent,
	onConsentGiven,
	isSmallScreen,
	isProduction,
}: BankSelectorPageLayoutProps) => {
	const { t } = useTranslation()

	const onBankSearchSuggest = useCallback(
		(searchTerm?: string) => handleSearch(banks, isProduction, searchTerm),
		[banks],
	)

	const favoriteBanks = useMemo(
		() =>
			recentlySearchedInitialCooldownComplete
				? banks
						.filter((bank) => bank.favorite)
						.slice(0, maxGridSize)
						.map((bank) => ({
							bannerSrc: bank.imageUrl,
							id: bank.id,
							name: bank.displayName,
						}))
				: null,
		[banks, recentlySearchedInitialCooldownComplete],
	)

	if (!consentGiven) {
		return (
			<MonthioConsent
				consent={translatedConsent}
				onAcceptChange={onConsentGiven}
			/>
		)
	}

	if (!banks.some((x) => x)) {
		return (
			<FlowPage>
				<FlowPage.Content $isCentered>
					<LoadingScreen />
				</FlowPage.Content>
			</FlowPage>
		)
	}

	return (
		<FlowPage>
			<FlowPage.Content>
				<Section $gap={isSmallScreen ? 'xxl' : '_3xl'}>
					<ButtonContainer>
						{bankSessions.bankSessions &&
						bankSessions.bankSessions.length > 0 ? (
							<PrimaryButton
								onClick={() =>
									history.replace(routeCodes.bankSessionOverviewPage)
								}
							>
								{t('bankSelector-return-button')}
							</PrimaryButton>
						) : null}
						{!isProduction && !demoProfile ? (
							<SecondaryButton
								onClick={() => {
									const monthioCustomBank = banks.filter(
										(bank) =>
											bank.serviceProviderId ===
											BankServiceProviders.CustomTest,
									)[0]!

									history.replace({
										pathname: routeCodes.bankLoginPage,
										state: {
											monthioInternalBankId: monthioCustomBank.id,
											bankId: monthioCustomBank.bankId,
										},
									})
								}}
							>
								{t('bankSelector-btn-addCustomBank')}
							</SecondaryButton>
						) : null}
					</ButtonContainer>

					<Section>
						<FlowPage.Header title={t('bankSelector-title')} />
						<LogoSearchBar
							translations={{
								clearButtonAriaLabel: t(
									'bankSelector-searchBankInput-clear-button',
								),
								noResults: t('bankSelector-searchBankInput-no-results'),
								placeHolder: t('bankSelector-searchBankInput-placeholder'),
							}}
							onSelect={(o) => onSelectBank(o.id)}
							onSuggest={onBankSearchSuggest}
						/>
					</Section>

					{favoriteBanks?.length ? (
						<Section>
							<H2>{t('bankSelector-popularBanks-title')}</H2>
							<BankSelect
								banks={favoriteBanks}
								onSelect={onSelectBank}
								isMobile={isSmallScreen}
							/>
						</Section>
					) : null}
				</Section>
			</FlowPage.Content>
		</FlowPage>
	)
}
